import { CartItem } from 'models/CartItem';
import {
  GtmAddToCartLocation,
  GtmItemProduct,
  GtmProduct,
  GtmPromotion,
  GtmSearchType
} from 'store/googleTagManager/types';
import { Action } from 'redux';
import { FormType } from 'models/FormType';
import { ICart } from 'models/ICart';

export const GTM_ADD_FAVOURITE = 'GTM_ADD_FAVOURITE';
export const GTM_ADD_PAYMENT_INFO = 'GTM_ADD_PAYMENT_INFO';
export const GTM_ADD_TO_CART = 'GTM_ADD_TO_CART';
export const GTM_CLEAR_CART = 'GTM_CLEAR_CART';
export const GTM_COPY_PRODUCT_TITLE = 'GTM_COPY_PRODUCT_TITLE';
export const GTM_FILTER_CHANGE = 'GTM_FILTER_CHANGE';
export const GTM_FOOTER_NAV_CLICK = 'GTM_FOOTER_NAV_CLICK';
export const GTM_INTERNAL_NAV_CLICK = 'GTM_INTERNAL_NAV_CLICK';
export const GTM_LOGIN = 'GTM_LOGIN';
export const GTM_REMOVE_FAVOURITE = 'GTM_REMOVE_FAVOURITE';
export const GTM_REMOVE_FROM_CART = 'GTM_REMOVE_FROM_CART';
export const GTM_SEARCH = 'GTM_SEARCH';
export const GTM_SELECT_ITEM = 'GTM_SELECT_ITEM';
export const GTM_SELECT_PROMOTION = 'GTM_SELECT_PROMOTION';
export const GTM_SELECT_THECA_PROMOTION = 'GTM_SELECT_THECA_PROMOTION';
export const GTM_VIEW_CART = 'GTM_VIEW_CART';
export const GTM_VIEW_LIST_ITEM = 'GTM_VIEW_LIST_ITEM';
export const GTM_VIEW_MORE_PRODUCTS = 'GTM_VIEW_MORE_PRODUCTS';
export const GTM_VIEW_PROMOTION = 'GTM_VIEW_PROMOTION';
export const GTM_VIEW_THECA_PROMOTION = 'GTM_VIEW_THECA_PROMOTION';
export const INSTALLATION_ZIP_CODE = 'GTM_INSTALLATION_ZIP_CODE';
export const NEWSLETTER_MODAL_DECLINE = 'NEWSLETTER_MODAL_DECLINE';
export const NEWSLETTER_MODAL_LATER = 'NEWSLETTER_MODAL_LATER';
export const NEWSLETTER_SIGNUP = 'NEWSLETTER_SIGNUP';
export const GTM_QUOTATION = 'GTM_QUOTATION';
export const GTM_QUOTATION_SUBMIT = 'GTM_QUOTATION_SUBMIT';
export const GTM_HELP_CENTER_FAQ = 'GTM_HELP_CENTER_FAQ';
export const GTM_HELP_CENTER_CATEGORY = 'GTM_HELP_CENTER_CATEGORY';
export const GTM_HELP_CENTER_QUESTION = 'GTM_HELP_CENTER_QUESTION';
export const GTM_CUSTOMER_SUPPORT_FORM_SUBMIT = 'GTM_CUSTOMER_SUPPORT_FORM_SUBMIT';
export const GTM_VOYADO_CART_UPDATE = 'GTM_VOYADO_CART_UPDATE';
export const GTM_POPULAR_CATEGORY_NAV = 'GTM_POPULAR_CATEGORY_NAV';
export const GTM_EDIT_QUANTITY = 'GTM_EDIT_QUANTITY';
export const GTM_VIEW_GROUP_ADDON = 'GTM_VIEW_GROUP_ADDON';
export const GTM_CHANGE_PAYMENT_METHOD = 'GTM_CHANGE_PAYMENT_METHOD';
export const GTM_VIEW_PRICE_MATCH_GUARANTEE = 'GTM_VIEW_PRICE_MATCH_GUARANTEE';
export const GTM_ADD_SHIPPING_INFO = 'GTM_ADD_SHIPPING_INFO';
export const GTM_CONTINUE_TO_PAYMENT = 'GTM_CONTINUE_TO_PAYMENT';
export const GTM_ADD_CONTACT_INFO = 'GTM_ADD_CONTACT_INFO';

export type GtmCartItemAction = {
  cartItem: CartItem;
  btnLocation?: GtmAddToCartLocation;
  itemListName?: string;
  amount?: number;
  addons?: CartItem[];
};

/**
 * Creates an action for removing an item from the cart.
 * @param {GtmCartItemAction} params - Parameters for removing from cart action.
 */
export const GtmRemoveFromCart = ({
  cartItem,
  itemListName,
  amount = 1,
  addons
}: GtmCartItemAction) => {
  return {
    type: GTM_REMOVE_FROM_CART,
    cartItem,
    itemListName,
    amount,
    addons
  };
};

/**
 * Creates an action for adding an item to the cart.
 * @param {GtmCartItemAction} params - Parameters for adding to cart action.
 */
export const GtmAddToCart = ({
  cartItem,
  btnLocation,
  itemListName,
  amount = 1,
  addons
}: GtmCartItemAction) => ({
  type: GTM_ADD_TO_CART,
  cartItem,
  btnLocation,
  itemListName,
  amount,
  addons
});

/**
 * Creates an action for clearing the cart.
 */
export const GtmClearCart = () => {
  return {
    type: GTM_CLEAR_CART
  };
};

type GtmItemProps = {
  gtmItemProduct: GtmItemProduct;
  listPageNbr?: number;
};

type GtmViewItemProps = {
  gtmItemProducts: GtmItemProduct[];
  listPageNbr?: number;
};

export type GtmItemAction = Action & GtmItemProps;

export type GtmViewItemAction = Action & {
  gtmItemProducts: GtmItemProduct[];
  listPageNbr?: number;
};

/**
 * Creates an action for selecting an item.
 * @param {GtmItemProps} params - Parameters for selecting item action.
 * @returns {GtmItemAction} Action object.
 */
export const GtmSelectItem = ({ gtmItemProduct, listPageNbr }: GtmItemProps): GtmItemAction => {
  return {
    type: GTM_SELECT_ITEM,
    gtmItemProduct,
    listPageNbr
  };
};

/**
 * Creates an action for viewing a list of items.
 * @param {GtmViewItemProps} params - Parameters for viewing list item action.
 * @returns {GtmViewItemAction} Action object.
 */
export const GtmViewListItem = ({
  gtmItemProducts,
  listPageNbr
}: GtmViewItemProps): GtmViewItemAction => {
  return {
    type: GTM_VIEW_LIST_ITEM,
    gtmItemProducts,
    listPageNbr
  };
};

export type GtmPromotionAction = Action & {
  gtmPromotion: GtmPromotion;
};

export type GtmThecaPromotionAction = Action & {
  gtmPromotions: GtmPromotion[];
};

/**
 * Creates an action for viewing a single GTM promotion.
 * @param {GtmPromotion} gtmPromotion - The GTM promotion object.
 * @returns {GtmPromotionAction} The action object.
 */
export const GtmViewPromotion = (gtmPromotion: GtmPromotion): GtmPromotionAction => {
  return {
    type: GTM_VIEW_PROMOTION,
    gtmPromotion
  };
};

/**
 * Creates an action for selecting a single GTM promotion.
 * @param {GtmPromotion} gtmPromotion - The GTM promotion object.
 * @returns {GtmPromotionAction} The action object.
 */
export const GtmSelectPromotion = (gtmPromotion: GtmPromotion): GtmPromotionAction => {
  return {
    type: GTM_SELECT_PROMOTION,
    gtmPromotion
  };
};

/**
 * Creates an action for viewing multiple GTM promotions.
 * @param {GtmPromotion[]} gtmPromotions - Array of GTM promotion objects.
 * @returns {GtmThecaPromotionAction} The action object.
 */
export const GtmViewThecaPromotions = (gtmPromotions: GtmPromotion[]): GtmThecaPromotionAction => {
  return {
    type: GTM_VIEW_THECA_PROMOTION,
    gtmPromotions
  };
};

/**
 * Creates an action for selecting a single GTM promotion from multiple promotions.
 * @param {GtmPromotion} gtmPromotion - The GTM promotion object.
 * @returns {GtmPromotionAction} The action object.
 */
export const GtmSelectThecaPromotion = (gtmPromotion: GtmPromotion): GtmPromotionAction => {
  return {
    type: GTM_SELECT_THECA_PROMOTION,
    gtmPromotion
  };
};

export type GtmFavouriteAction = Action & {
  gtmProduct: GtmProduct;
};

/**
 * Creates an action for adding a product to favorites.
 * @param {GtmProduct} gtmProduct - The GTM product to add to favorites.
 * @returns {GtmFavouriteAction} The action object.
 */
export const GtmAddFavourite = (gtmProduct: GtmProduct): GtmFavouriteAction => {
  return {
    type: GTM_ADD_FAVOURITE,
    gtmProduct
  };
};

/**
 * Creates an action for adding payment information.
 * @returns {Action} Action object.
 */
export const GtmAddPaymentInfo = (): Action => {
  return {
    type: GTM_ADD_PAYMENT_INFO
  };
};

/**
 * Creates an action for viewing the basket.
 * @returns {Action} Action object.
 */
export const GtmViewBasket = (): Action => {
  return {
    type: GTM_VIEW_CART
  };
};

/**
 * Creates an action for later interaction with newsletter modal.
 * @returns {Action} Action object.
 */
export const GtmNewsletterModalLater = (): Action => {
  return { type: NEWSLETTER_MODAL_LATER };
};

/**
 * Creates an action for declining newsletter modal.
 * @returns {Action} Action object.
 */
export const GtmNewsletterModalDecline = (): Action => {
  return { type: NEWSLETTER_MODAL_DECLINE };
};

export type GtmNewsletterSignupAction = Action & {
  signupPlacement: string;
};

/**
 * Creates an action for newsletter signup.
 * @param {string} signupPlacement - Placement of the signup.
 * @returns {GtmNewsletterSignupAction} Action object.
 */
export const GtmNewsletterSignup = (signupPlacement: string): GtmNewsletterSignupAction => {
  return { type: NEWSLETTER_SIGNUP, signupPlacement };
};

export type GtmCopyProductNameAction = Action & {
  title: string;
  productId: string;
};

/**
 * Creates an action for copying product title.
 * @param {string} title - Product title.
 * @param {string} productId - Product ID.
 * @returns {GtmCopyProductNameAction} Action object.
 */
export const GtmCopyProductTitle = (title: string, productId: string): GtmCopyProductNameAction => {
  return {
    type: GTM_COPY_PRODUCT_TITLE,
    title,
    productId
  };
};

/**
 * Creates an action for filter change.
 * @returns {Action} Action object.
 */
export const GtmFilterChange = (): Action => {
  return {
    type: GTM_FILTER_CHANGE
  };
};

export type GtmSearchAction = Action & {
  search_term: string;
  search_type: GtmSearchType;
};

/**
 * Creates an action for search.
 * @param {string} searchTerm - Search term.
 * @param {GtmSearchType} searchType - Type of search.
 * @returns {GtmSearchAction} Action object.
 */
export const GtmSearch = (searchTerm: string, searchType: GtmSearchType): GtmSearchAction => {
  return {
    type: GTM_SEARCH,
    search_term: searchTerm,
    search_type: searchType
  };
};

export const GtmRemoveFavourite = (gtmProduct: GtmProduct): GtmFavouriteAction => {
  return {
    type: GTM_REMOVE_FAVOURITE,
    gtmProduct
  };
};

/**
 * Creates an action for login into Customer Pages.
 * @returns {Action} Action object.
 */
export const GtmLogin = (): Action => {
  return {
    type: GTM_LOGIN
  };
};

/**
 * Creates an action for viewing more products in a product list.
 * @returns {Action} Action object.
 */
export const GtmViewMoreProducts = (): Action => {
  return {
    type: GTM_VIEW_MORE_PRODUCTS
  };
};

export interface InstallationZipCodeAction extends Action {
  installationAvailable: boolean;
}

/**
 * Creates an action for setting installation zip code.
 * @param {boolean} installationAvailable - Whether installation is available.
 * @returns {InstallationZipCodeAction} Action object.
 */
export const GTMInstallationZipCode = (
  installationAvailable: boolean
): InstallationZipCodeAction => ({
  type: INSTALLATION_ZIP_CODE,
  installationAvailable
});

export interface GtmInternalNavAction extends Action {
  link_url: string;
}

/**
 * Creates an action for header navigation click.
 * @param {string} url - URL of the navigation.
 * @returns {GtmInternalNavAction} Action object.
 */
export const GtmHeaderNavClick = (url: string): GtmInternalNavAction => {
  return {
    type: GTM_INTERNAL_NAV_CLICK,
    link_url: url
  };
};

/**
 * Creates an action for footer navigation click.
 * @param {string} url - URL of the navigation.
 * @returns {GtmInternalNavAction} Action object.
 */
export const GtmFooterNavClick = (url: string): GtmInternalNavAction => {
  return {
    type: GTM_FOOTER_NAV_CLICK,
    link_url: url
  };
};

export type GtmQuotationRequestAction = Action & {
  productName: string;
  url: string;
  brandName: string;
};

/**
 * Creates an action for clicking CTA for quotation request.
 * @param {string} productName - Name of the product.
 * @param {string} url - URL of the page.
 * @param {string} brandName - Name of the brand.
 * @returns {GtmQuotationRequestAction} Action object.
 */
export const GtmQuotationRequest = (
  productName: string,
  url: string,
  brandName: string
): GtmQuotationRequestAction => {
  return {
    type: GTM_QUOTATION,
    productName,
    brandName,
    url
  };
};

/**
 * Creates an action for submitting a quotation request.
 * @returns {Action} Action object.
 */
export const GtmQuotationRequestSubmit = (): Action => {
  return {
    type: GTM_QUOTATION_SUBMIT
  };
};

export type GtmHelpCenterFaqAction = Action & {
  question: string;
};

export type GtmHelpCenterCategoryAction = Action & {
  category: string;
};

export type GtmHelpCenterQuestionAction = Action & {
  category: string;
  question: string;
};

/**
 * Creates an action for clicking on an FAQ
 * @param {string} question - The heading of the question
 * @returns {GtmHelpCenterFaqAction} - Action object
 */
export const GtmHelpCenterFaqClick = (question: string): GtmHelpCenterFaqAction => {
  return {
    type: GTM_HELP_CENTER_FAQ,
    question
  };
};

/**
 * Creates an action for clicking on a category
 * @param {string} category - The category of questions
 * @returns {GtmHelpCenterCategoryAction} - Action object
 */
export const GtmHelpCenterCategoryClick = (category: string): GtmHelpCenterCategoryAction => {
  return {
    type: GTM_HELP_CENTER_CATEGORY,
    category
  };
};

/**
 * Creates an action for when clicking on a question below a category
 * @param category - The category of the question
 * @param question - The button text for the question
 * @returns {GtmHelpCenterQuestionAction} - Action object
 */
export const GtmHelpCenterQuestionClick = (
  category: string,
  question: string
): GtmHelpCenterQuestionAction => {
  return {
    type: GTM_HELP_CENTER_QUESTION,
    category,
    question
  };
};

export type GtmCustomerSupportAction = Action & {
  form: FormType;
};

/**
 * Creates an action when submitting a form
 * @param form - The type of form
 * @returns {GtmCustomerSupportAction} - Action object
 */
export const GtmCustomerSupportFormSubmit = (form: FormType): GtmCustomerSupportAction => {
  return {
    type: GTM_CUSTOMER_SUPPORT_FORM_SUBMIT,
    form
  };
};

export type GtmVoyadoCartUpdateAction = Action & {
  cart?: ICart;
};

/**
 * Creates an action for when we want to send a Voyado Cart event.
 * If no cart is provided the cart from redux state will be used
 * @returns {Action} - Action object
 */
export const GtmVoyadoCartUpdate = (cart?: ICart): GtmVoyadoCartUpdateAction => {
  return {
    type: GTM_VOYADO_CART_UPDATE,
    cart
  };
};

export interface GtmPopularCategoryNavAction extends Action {
  link_url: string;
  title: string;
}

/**
 * Creates an action for popular category navigation.
 * @param {string} url - URL of the navigation.
 * @param {string} title - The category title that is clicked.
 * @returns {GtmPopularCategoryNavAction} Action object.
 */
export const GtmPopularCategoryNavClick = (
  url: string,
  title: string
): GtmPopularCategoryNavAction => {
  return {
    type: GTM_POPULAR_CATEGORY_NAV,
    link_url: url,
    title
  };
};

export type GtmQuantityAction = Action & {
  gtmProduct: GtmProduct;
  quantity: number;
  prevQuantity: number;
};

/**
 * Creates an action for updating the quantity.
 * @param {GtmProduct} gtmProduct - The GTM product whose quantity is being updated.
 * @param quantity - The new quantity of the product.
 * @param prevQuantity - The previous quantity of the product.
 * @returns {GtmQuantityAction} The action object.
 */
export const GtmEditQuantity = (
  gtmProduct: GtmProduct,
  quantity: number,
  prevQuantity: number
): GtmQuantityAction => {
  return {
    type: GTM_EDIT_QUANTITY,
    gtmProduct,
    quantity,
    prevQuantity
  };
};

export type GtmViewGroupAddonAction = Action & {
  groupAddons: string[];
  productId: string;
};

/**
 * Creates an action for viewing a group addon.
 * @param groupAddons - The addon group that is viewed.
 * @param productId - The product id of the product page where the addon group is on.
 * @returns {GtmViewGroupAddonAction} The action object.
 */
export const GtmViewGroupAddon = (
  groupAddons: string[],
  productId: string
): GtmViewGroupAddonAction => {
  return {
    type: GTM_VIEW_GROUP_ADDON,
    groupAddons,
    productId
  };
};

export type GtmChangePaymentMethodAction = Action & {
  paymentMethod: string;
};

/**
 * Creates an action when changing the payment method during checkout
 * @param {string} paymentMethod - the new selected payment method
 * @returns {Action} - Action object
 */
export const GtmChangePaymentMethod = (paymentMethod: string): GtmChangePaymentMethodAction => {
  return {
    type: GTM_CHANGE_PAYMENT_METHOD,
    paymentMethod
  };
};

/**
 * Creates an action for viewing price match guarantee.
 * @returns {Action} - Action object
 */
export const GtmViewPriceMatchGuarantee = (): Action => {
  return {
    type: GTM_VIEW_PRICE_MATCH_GUARANTEE
  };
};

/**
 * Creates an action when submitting a purchase in the checkout
 * @returns {Action} - Action object
 */
export const GtmContinueToPayment = (): Action => {
  return {
    type: GTM_CONTINUE_TO_PAYMENT
  };
};

/**
 * Creates an action for viewing or changing the contact info
 * @returns {Action} - Action object
 */
export const GtmAddContactInfo = (): Action => {
  return {
    type: GTM_ADD_CONTACT_INFO
  };
};

/**
 * Creates an action for viewing or changing the shipping info
 * @returns {Action} - Action object
 */
export const GtmAddShippingInfo = (): Action => {
  return {
    type: GTM_ADD_SHIPPING_INFO
  };
};
